import { useEffect, useRef, useState } from 'react';
import { lockScroll } from '../lib/scroll-lock';
import { Link, useNavigate } from 'react-router-dom';
import { X, Eye, EyeOff } from 'lucide-react';
import NoAccountHint from './NoAccountHint';

export default function SignInModal({ onClose }) {
  const [closing, setClosing] = useState(false);
  const closingRef = useRef(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const navigate = useNavigate();

  const requestClose = () => {
    if (closingRef.current) return;
    closingRef.current = true;
    setClosing(true);
    setTimeout(onClose, 320);
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') requestClose(); };
    document.addEventListener('keydown', onKey);
    const unlock = lockScroll();
    return () => {
      document.removeEventListener('keydown', onKey);
      unlock();
    };
  }, []);

  async function submit(e) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error === 'account_locked'
          ? 'Too many failed attempts. Try again in 30 minutes.'
          : 'Invalid email or password.');
        setShowHint(true);
        return;
      }
      localStorage.setItem('learnable_user', JSON.stringify(data.user));
      requestClose();
      navigate(data.user.is_admin ? '/admin' : '/dashboard');
    } catch {
      setError('Could not reach the server. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Sign in"
      onClick={requestClose}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60"
      style={{
        animation: closing
          ? 'carouselFadeOut 0.3s ease-in forwards'
          : 'carouselFade 0.25s ease-out both',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative bg-white rounded-3xl shadow-2xl max-w-md w-full p-7 lg:p-8"
        style={{
          animation: closing
            ? 'modalSlideDown 0.3s cubic-bezier(0.32, 0, 0.67, 0) forwards'
            : 'fadeUp 0.3s cubic-bezier(0.22, 1, 0.36, 1) both',
        }}
      >
        <button
          type="button"
          onClick={requestClose}
          aria-label="Close"
          className="absolute top-4 right-4 w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-slate-900 transition"
        >
          <X size={18} />
        </button>

        <h2 className="text-2xl font-extrabold text-slate-900">Welcome back</h2>
        <p className="text-sm text-slate-500 mt-1">Sign in to your Learnable account.</p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label
              htmlFor="signin-email"
              className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1.5"
            >
              Email
            </label>
            <input
              id="signin-email"
              type="email"
              required
              autoFocus
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl border-2 border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition"
            />
          </div>

          <div>
            <div className="flex items-baseline justify-between mb-1.5">
              <label
                htmlFor="signin-password"
                className="block text-xs font-bold uppercase tracking-widest text-slate-500"
              >
                Password
              </label>
              <Link
                to="/forgot-password"
                onClick={requestClose}
                className="text-xs font-bold text-brand hover:text-brand-dark"
              >
                Forgot password?
              </Link>
            </div>
            <div className="relative">
              <input
                id="signin-password"
                type={showPassword ? 'text' : 'password'}
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-xl border-2 border-slate-200 px-4 py-2.5 pr-11 text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {error && (
            <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
          {showHint && <NoAccountHint onNavigate={requestClose} />}

          <button
            type="submit"
            disabled={busy}
            className="w-full bg-brand text-white rounded-xl py-3 font-bold hover:bg-brand-dark disabled:opacity-50 shadow-lg shadow-brand/20 transition"
          >
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <div className="mt-5 pt-5 border-t border-slate-100 text-center text-sm text-slate-500">
          Don't have an account?{' '}
          <Link
            to="/signup"
            onClick={requestClose}
            className="text-brand font-bold hover:text-brand-dark"
          >
            Create one
          </Link>
        </div>
      </div>
    </div>
  );
}
